import React from "react";
import { useTranslation } from "react-i18next";

import { object } from "prop-types";

import { maskMoney, reduce } from "~/hooks/helpers";
import { Tooltip } from "~/components";

import {
  Container,
  Header,
  InfoSection,
  BlockTitle,
  StatusSection,
  SectionContainer,
  StatusTitle,
  PartyItem,
  PartyName,
  PartyDocument,
  PartyType,
  ValueSection,
  ValueLabel,
  ValueAmount,
} from "./styles";

const ProcessoJudicial = ({ data, isCaseCoverScreen = false }) => {
  const { t } = useTranslation("popups");

  const formatValue = (value) =>
    !value || value < 0 ? "Não disponível" : maskMoney(value);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      // Se a data já está no formato brasileiro, retorna como está
      if (dateString.includes("/")) return dateString;
      // Se está no formato ISO, converte para brasileiro
      const date = new Date(dateString);
      return date.toLocaleDateString("pt-BR");
    } catch {
      return dateString || "N/A";
    }
  };

  const formatDocument = (document) => {
    if (!document) return "N/A";

    // Remove todos os caracteres que não são números
    const numbersOnly = document.replace(/\D/g, "");

    // Se tem 11 dígitos, formata como CPF
    if (numbersOnly.length === 11) {
      return numbersOnly.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
    }

    // Se tem 14 dígitos, formata como CNPJ
    if (numbersOnly.length === 14) {
      return numbersOnly.replace(
        /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
        "$1.$2.$3/$4-$5"
      );
    }

    // Se não tem o tamanho esperado, retorna como estava
    return document;
  };

  const DocumentIcon = () => (
    <svg
      width="30"
      height="30"
      viewBox="0 0 44 44"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M29.3333 3.66663H14.6667C8.25 3.66663 5.5 7.33329 5.5 12.8333V31.1666C5.5 36.6666 8.25 40.3333 14.6667 40.3333H29.3333C35.75 40.3333 38.5 36.6666 38.5 31.1666V12.8333C38.5 7.33329 35.75 3.66663 29.3333 3.66663ZM14.6667 22.4583H22C22.7517 22.4583 23.375 23.0816 23.375 23.8333C23.375 24.585 22.7517 25.2083 22 25.2083H14.6667C13.915 25.2083 13.2917 24.585 13.2917 23.8333C13.2917 23.0816 13.915 22.4583 14.6667 22.4583ZM29.3333 32.5416H14.6667C13.915 32.5416 13.2917 31.9183 13.2917 31.1666C13.2917 30.415 13.915 29.7916 14.6667 29.7916H29.3333C30.085 29.7916 30.7083 30.415 30.7083 31.1666C30.7083 31.9183 30.085 32.5416 29.3333 32.5416ZM33.9167 16.9583H30.25C27.4633 16.9583 25.2083 14.7033 25.2083 11.9166V8.24996C25.2083 7.49829 25.8317 6.87496 26.5833 6.87496C27.335 6.87496 27.9583 7.49829 27.9583 8.24996V11.9166C27.9583 13.1816 28.985 14.2083 30.25 14.2083H33.9167C34.6683 14.2083 35.2917 14.8316 35.2917 15.5833C35.2917 16.335 34.6683 16.9583 33.9167 16.9583Z"
        fill="#414251"
      />
    </svg>
  );

  const GraphIcon = () => (
    <svg
      width="30"
      height="30"
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M36.6668 36.6666H3.3335C2.65016 36.6666 2.0835 36.1 2.0835 35.4166C2.0835 34.7333 2.65016 34.1666 3.3335 34.1666H36.6668C37.3502 34.1666 37.9168 34.7333 37.9168 35.4166C37.9168 36.1 37.3502 36.6666 36.6668 36.6666Z"
        fill="#414251"
      />
      <path
        d="M16.25 6.66671V36.6667H23.75V6.66671C23.75 4.83337 23 3.33337 20.75 3.33337H19.25C17 3.33337 16.25 4.83337 16.25 6.66671Z"
        fill="#414251"
      />
      <path
        d="M5 16.6667V36.6667H11.6667V16.6667C11.6667 14.8334 11 13.3334 9 13.3334H7.66667C5.66667 13.3334 5 14.8334 5 16.6667Z"
        fill="#414251"
      />
      <path
        d="M28.3335 25V36.6666H35.0002V25C35.0002 23.1666 34.3335 21.6666 32.3335 21.6666H31.0002C29.0002 21.6666 28.3335 23.1666 28.3335 25Z"
        fill="#414251"
      />
    </svg>
  );

  const DollarIcon = () => (
    <svg
      width="30"
      height="30"
      viewBox="0 0 35 35"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M16.4061 11.7833V15.9542L14.9331 15.4437C14.1894 15.1812 13.7373 14.9333 13.7373 13.6646C13.7373 12.6292 14.5102 11.7833 15.4581 11.7833H16.4061Z"
        fill="#414251"
      />
      <path
        d="M21.2625 21.3354C21.2625 22.3708 20.4896 23.2166 19.5417 23.2166H18.5938V19.0458L20.0667 19.5562C20.8104 19.8187 21.2625 20.0666 21.2625 21.3354Z"
        fill="#414251"
      />
      <path
        d="M23.6103 2.91663H11.3894C6.08109 2.91663 2.9165 6.08121 2.9165 11.3895V23.6104C2.9165 28.9187 6.08109 32.0833 11.3894 32.0833H23.6103C28.9186 32.0833 32.0832 28.9187 32.0832 23.6104V11.3895C32.0832 6.08121 28.9186 2.91663 23.6103 2.91663ZM20.7957 17.5C21.9332 17.8937 23.4498 18.725 23.4498 21.3354C23.4498 23.5812 21.6998 25.4041 19.5415 25.4041H18.5936V26.25C18.5936 26.8479 18.0978 27.3437 17.4998 27.3437C16.9019 27.3437 16.4061 26.8479 16.4061 26.25V25.4041H15.8811C13.4894 25.4041 11.5498 23.377 11.5498 20.8979C11.5498 20.3 12.0311 19.8041 12.6436 19.8041C13.2415 19.8041 13.7373 20.3 13.7373 20.8979C13.7373 22.1812 14.6998 23.2166 15.8811 23.2166H16.4061V18.2729L14.204 17.5C13.0665 17.1062 11.5498 16.275 11.5498 13.6645C11.5498 11.4187 13.2998 9.59579 15.4582 9.59579H16.4061V8.74996C16.4061 8.15204 16.9019 7.65621 17.4998 7.65621C18.0978 7.65621 18.5936 8.15204 18.5936 8.74996V9.59579H19.1186C21.5103 9.59579 23.4498 11.6229 23.4498 14.102C23.4498 14.7 22.9686 15.1958 22.3561 15.1958C21.7582 15.1958 21.2623 14.7 21.2623 14.102C21.2623 12.8187 20.2998 11.7833 19.1186 11.7833H18.5936V16.727L20.7957 17.5Z"
        fill="#414251"
      />
    </svg>
  );

  const DefendantIcon = () => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M18.5002 4.10995L13.5102 2.23995C12.6802 1.92995 11.3202 1.92995 10.4902 2.23995L5.50016 4.10995C4.35016 4.53995 3.41016 5.89995 3.41016 7.11995V14.5499C3.41016 15.7299 4.19016 17.2799 5.14016 17.9899L9.44016 21.1999C10.8502 22.2599 13.1702 22.2599 14.5802 21.1999L18.8802 17.9899C19.8302 17.2799 20.6102 15.7299 20.6102 14.5499V7.11995C20.5902 5.89995 19.6502 4.53995 18.5002 4.10995ZM11.9302 7.02995C13.1102 7.02995 14.0702 7.98995 14.0702 9.16995C14.0702 10.3299 13.1602 11.2599 12.0102 11.2999H11.9902H11.9702C11.9502 11.2999 11.9302 11.2999 11.9102 11.2999C10.7102 11.2599 9.81016 10.3299 9.81016 9.16995C9.80016 7.98995 10.7602 7.02995 11.9302 7.02995ZM14.1902 16.3599C13.5802 16.7599 12.7902 16.9699 12.0002 16.9699C11.2102 16.9699 10.4102 16.7699 9.81016 16.3599C9.24016 15.9799 8.93016 15.4599 8.92016 14.8899C8.92016 14.3299 9.24016 13.7899 9.81016 13.4099C11.0202 12.6099 12.9902 12.6099 14.2002 13.4099C14.7702 13.7899 15.0902 14.3099 15.0902 14.8799C15.0802 15.4399 14.7602 15.9799 14.1902 16.3599Z"
        fill="#414251"
      />
    </svg>
  );

  const NeutralIcon = () => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M22 21.2499H20.75V10.9999C20.75 8.57994 19.42 7.24994 17 7.24994H12.75V6.01994C13.33 6.15994 13.91 6.23994 14.5 6.23994C15.44 6.23994 16.38 6.05994 17.28 5.69994C17.56 5.58994 17.75 5.30994 17.75 4.99994V1.99994C17.75 1.74994 17.63 1.51994 17.42 1.37994C17.21 1.23994 16.95 1.20994 16.72 1.29994C15.29 1.86994 13.71 1.86994 12.28 1.29994C12.05 1.20994 11.79 1.23994 11.58 1.37994C11.37 1.51994 11.25 1.74994 11.25 1.99994V4.99994V7.24994H7C4.58 7.24994 3.25 8.57994 3.25 10.9999V21.2499H2C1.59 21.2499 1.25 21.5899 1.25 21.9999C1.25 22.4099 1.59 22.7499 2 22.7499H4H20H22C22.41 22.7499 22.75 22.4099 22.75 21.9999C22.75 21.5899 22.41 21.2499 22 21.2499ZM7.24 21.2499H4.75V12.7499H7.24V21.2499ZM11.24 21.2499H8.74V12.7499H11.24V21.2499ZM15.24 21.2499H12.74V12.7499H15.24V21.2499ZM19.25 21.2499H16.74V12.7499H19.25V21.2499Z"
        fill="#414251"
      />
    </svg>
  );

  const TargetIcon = () => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M22 11.25H19.96C19.6 7.44 16.56 4.39 12.75 4.04V2C12.75 1.59 12.41 1.25 12 1.25C11.59 1.25 11.25 1.59 11.25 2V4.04C7.44 4.4 4.39 7.44 4.04 11.25H2C1.59 11.25 1.25 11.59 1.25 12C1.25 12.41 1.59 12.75 2 12.75H4.04C4.4 16.56 7.44 19.61 11.25 19.96V22C11.25 22.41 11.59 22.75 12 22.75C12.41 22.75 12.75 22.41 12.75 22V19.96C16.56 19.6 19.61 16.56 19.96 12.75H22C22.41 12.75 22.75 12.41 22.75 12C22.75 11.59 22.41 11.25 22 11.25ZM12 15.12C10.28 15.12 8.88 13.72 8.88 12C8.88 10.28 10.28 8.88 12 8.88C13.72 8.88 15.12 10.28 15.12 12C15.12 13.72 13.72 15.12 12 15.12Z"
        fill="#414251"
      />
    </svg>
  );

  // Organiza as partes por polaridade
  const partiesByPolarity = {
    active:
      data.Parties?.filter(
        (party) => party.Polarity?.toLowerCase() === "active"
      ) || [],
    passive:
      data.Parties?.filter(
        (party) => party.Polarity?.toLowerCase() === "passive"
      ) || [],
    neutral:
      data.Parties?.filter(
        (party) => party.Polarity?.toLowerCase() === "neutral"
      ) || [],
  };

  const sectionConfig = [
    {
      key: "active",
      title: "Autores",
      parties: partiesByPolarity.active,
      color: "red",
    },
    {
      key: "passive",
      title: "Réus",
      parties: partiesByPolarity.passive,
      color: "orange",
    },
    {
      key: "neutral",
      title: "Neutro",
      parties: partiesByPolarity.neutral,
      color: "blue",
    },
  ];

  return (
    <Container>
      {/* Header com informações do processo */}
      <div style={{ display: "flex", gap: "15px", alignItems: "stretch" }}>
        {/* Bloco grande à esquerda - Processo */}
        <InfoSection style={{ flex: 1, minHeight: "300px" }}>
          <BlockTitle>
            <DocumentIcon />
            <strong>Processo</strong>
          </BlockTitle>
          <div>
            <strong>Data distribuição:</strong>{" "}
            {formatDate(data.NoticeDate) || "Não disponível"}
          </div>
          <div>
            <strong>Tribunal:</strong> {data.CourtName || "Não disponível"}
          </div>
          <div>
            <strong>Estado:</strong> {data.State || "Não disponível"}
          </div>
          <div>
            <strong>Vara:</strong> {data.JudgingBody || "Não disponível"}
          </div>
          <div>
            <strong>Classe:</strong> {data.CourtType || "Não disponível"}
          </div>
          <div>
            <strong>Natureza:</strong> {data.Nature || "Não disponível"}
          </div>
          <div>
            <strong>Assunto:</strong>{" "}
            {data.MainSubject && data.MainSubject.length > 100 ? (
              <Tooltip label={data.MainSubject} width={400}>
                <span>{reduce(data.MainSubject, 100)}</span>
              </Tooltip>
            ) : (
              data.MainSubject || "Não disponível"
            )}
          </div>
          {isCaseCoverScreen &&   
            <div>
              <strong>Link do processo:</strong>{" "}
              {
                data.CaseUrl ? (
                  <a href={data.CaseUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline', color: '#007bff' }}>
                    {data.CaseUrl}
                  </a>
                ) : (
                  "Não disponível"
                )
              }
            </div>
          }
        
        </InfoSection>

        {/* Blocos à direita */}
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            minHeight: "300px",
          }}
        >
          {/* Status atual */}
          <InfoSection style={{ flex: 1, justifyContent: "flex-start" }}>
            <BlockTitle>
              <GraphIcon />
              <strong>Status atual</strong>
            </BlockTitle>
            <div>
              <strong>Status:</strong> {data.Status || "Não disponível"}
            </div>
            <div>
              <strong>Data última atualização:</strong>{" "}
              {formatDate(data.LastUpdate) || "Não disponível"}
            </div>
          </InfoSection>

          {/* Valor da causa */}
          <ValueSection>
            <BlockTitle>
              <DollarIcon />
              <strong>Valor da causa</strong>
            </BlockTitle>
            <div>
              <strong>Valor:</strong>{" "}
              {data.Value ? formatValue(data.Value) : "Não disponível"}
            </div>
          </ValueSection>
        </div>
      </div>

      {/* Seções de partes */}
      <div style={{ marginTop: "20px" }}>
        {sectionConfig
          .filter((section) => section.parties.length > 0)
          .map((section) => (
            <SectionContainer key={section.key}>
              <StatusTitle color={section.color}>
                {section.title}
                {section.key === "passive" && <DefendantIcon />}
                {section.key === "neutral" && <NeutralIcon />}
                {section.key === "active" && <TargetIcon />}
              </StatusTitle>
              <StatusSection color={section.color}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      fontWeight: "bold",
                      borderBottom: "1px solid #eee",
                      paddingBottom: "3px",
                      fontSize: "14px",
                    }}
                  >
                    <div style={{ flex: 1 }}>Nome</div>
                    <div style={{ flex: 1 }}>Documento</div>
                    <div style={{ flex: 1 }}>Tipo</div>
                  </div>
                  {section.parties.map((party, index) => (
                    <PartyItem key={index}>
                      <PartyName>{party.Name || "N/A"}</PartyName>
                      <PartyDocument>{formatDocument(party.Doc)}</PartyDocument>
                      <PartyType>
                        {party.PartyDetails?.SpecificType ||
                          party.Type ||
                          "N/A"}
                      </PartyType>
                    </PartyItem>
                  ))}
                </div>
              </StatusSection>
            </SectionContainer>
          ))}
      </div>
    </Container>
  );
};

ProcessoJudicial.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  data: object.isRequired,
};

export default ProcessoJudicial;
